import React, { useState } from 'react';
import axios from 'axios';
import './FishDetails.css'; 

// A new component to display the search results cleanly.
const SearchResult = ({ details, imageUrl, fishName }) => {
  // This function is now corrected to properly handle markdown formatting.
  const formatContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];

    // Helper function to push a complete list to the main elements array
    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(<ul key={`ul-${elements.length}`}>{listItems}</ul>);
        listItems = []; // Reset the list
      }
    };

    lines.forEach((line, index) => {
      // Check if the line is a list item
      if (line.trim().startsWith('* ')) {
        const listItemText = line.trim().substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(<li key={index} dangerouslySetInnerHTML={{ __html: listItemText }} />);
      } else {
        // If it's not a list item, first render any existing list
        flushList();
        // Then render the paragraph if it's not empty
        if (line.trim() !== '') {
          const paragraph = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          elements.push(<p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />);
        }
      }
    });

    // After the loop, render any remaining list items
    flushList();

    return elements;
  };

  return (
    <div className="results-container animate-fade-in">
      <div className="result-image">
        <img src={imageUrl} alt={fishName} />
      </div>
      <div className="result-details">
        <h2>{fishName}</h2>
        {formatContent(details)}
      </div>
    </div>
  );
};

const SUGGESTED_SEARCHES = ['Clownfish', 'Blue Whale', 'Great White Shark', 'Seahorse', 'Manta Ray', 'Jellyfish'];

const FishDetails = () => {
  const [query, setQuery] = useState(''); 
  const [fishDetails, setFishDetails] = useState(null); 
  const [error, setError] = useState(''); 
  const [loading, setLoading] = useState(false); 
  const [imageUrl, setImageUrl] = useState(''); 

  const handleSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q) return;
    setQuery(q);

    setLoading(true);
    setError('');
    setFishDetails(null);
    setImageUrl('');

    try {
      // Fetch details from Groq API
      const groqApiKey = process.env.REACT_APP_GROQ_API_KEY;
      const response = await axios.post(
        'https://api.groq.com/openai/v1/chat/completions',
        {
          model: 'llama-3.3-70b-versatile',
          messages: [
            {
              role: 'system',
              content: 'You are a marine biology expert. Provide detailed, well-structured information about marine life. Use markdown formatting with **bold** for key terms and * for bullet points. Include sections on habitat, diet, physical characteristics, conservation status, and interesting facts.'
            },
            {
              role: 'user',
              content: `Tell me about the ${q} (marine/aquatic species). Provide comprehensive details.`
            }
          ],
          temperature: 0.7,
          max_tokens: 1024,
        },
        {
          headers: {
            'Authorization': `Bearer ${groqApiKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.choices && response.data.choices.length > 0) {
        setFishDetails(response.data.choices[0].message.content);
      } else {
        throw new Error('No details found for this species.');
      }

      // Use a free image search via Wikipedia API
      try {
        const wikiResponse = await axios.get(
          `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(q)}`
        );
        if (wikiResponse.data.thumbnail && wikiResponse.data.thumbnail.source) {
          // Get higher resolution by modifying the URL
          setImageUrl(wikiResponse.data.originalimage?.source || wikiResponse.data.thumbnail.source);
        }
      } catch (imgErr) {
        // Image fetch failed silently, we still show the text details
        console.log('Image not found, continuing without image');
      }

    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fish-details-page">
        <div className="video-bg-container">
            <video autoPlay loop muted playsInline className="video-bg">
                <source src="https://cdn.pixabay.com/video/2022/08/19/128421-741495470_large.mp4" type="video/mp4" />
            </video>
            <div className="video-overlay"></div>
        </div>

        <div className="content-wrapper">
            <div className="search-container">
                <span className="fish-badge">🐠 Marine Encyclopedia</span>
                <h1>Marine Life Encyclopedia</h1>
                <p className="subtitle">Discover the wonders of the ocean, one species at a time.</p>
                <div className="input-group">
                <input
                    type="text"
                    placeholder="e.g., Clownfish, Great White Shark..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button onClick={() => handleSearch()} disabled={loading || !query}>
                    {loading ? <div className="loader-small"></div> : 'Explore'}
                </button>
                </div>

                {/* Suggested Searches */}
                {!fishDetails && !loading && (
                  <div className="suggested-searches">
                    <span className="suggested-label">Popular searches:</span>
                    <div className="suggested-tags">
                      {SUGGESTED_SEARCHES.map(s => (
                        <button key={s} className="suggested-tag" onClick={() => handleSearch(s)}>{s}</button>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            
            {loading && <div className="loader-large"></div>}
            {error && <p className="error-message">{error}</p>}
            
            {fishDetails && (
                <SearchResult
                    details={fishDetails}
                    imageUrl={imageUrl}
                    fishName={query}
                />
            )}
        </div>
    </div>
  );
};

export default FishDetails;